import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import type { AuditListInput } from '../domain/auditFilter.js';
import { useTRPC } from '../utils/trpc.js';
import { CURSOR_PAGING } from './usePagedRows.js';

type Outputs = inferRouterOutputs<AppRouter>;

/** One row of `audit.list`, inferred off `AppRouter` rather than redeclared. No `input` — see `useAuditEntry`. */
export type AuditEntryView = Outputs['audit']['list']['entries'][number];

/** `audit.get`'s own return — a list row's full detail, `input` included. */
export type AuditEntryDetailView = Outputs['audit']['get'];

/**
 * Whether the audit log is switched on at all — `AUDIT_DYNAMO_TABLE` set or not — and the month
 * its two GSIs cover. `audit.status` is the one procedure that never refuses: it is how the nav
 * item, the page and the filter's Resource/Action selects decide whether to show anything before
 * the rest of the page has loaded.
 */
export const useAuditConfig = () => {
    const trpc = useTRPC();
    // `staleTime: Infinity` — see `hooks/useWriteAccess.ts`.
    const query = useQuery(trpc.audit.status.queryOptions(undefined, { meta: { quiet: true }, staleTime: Infinity }));
    return {
        configured: query.data?.configured ?? false,
        indexSince: query.data?.indexSince ?? null,
        loading: query.isLoading,
    };
};

/**
 * The resource types and actions the mutations declare (`router/audit.ts#auditVocabularyOf`) —
 * both sorted, used to populate the Resource / Action selects. `opts.enabled` lets the page hold
 * this off until `audit.status` says the feature is on.
 */
export const useAuditVocabulary = (opts: { enabled?: boolean } = {}) => {
    const trpc = useTRPC();
    const query = useQuery(
        trpc.audit.vocabulary.queryOptions(undefined, { meta: { quiet: true }, enabled: opts.enabled ?? true }),
    );
    return query.data?.resourceTypes ?? [];
};

/**
 * The audit log page's own list, paged by cursor (`audit.list`'s `nextCursor`).
 * `placeholderData: keepPreviousData`: a filter change is a new query key, and without it the
 * page would render the gap as an empty list mid-change rather than the previous filter's rows
 * standing in until the new page lands. `opts.enabled` lets the page hold the query off until
 * `audit.status` says the feature is configured. Its data is the entries of every page loaded,
 * flattened.
 */
export const useAuditList = (input: AuditListInput, opts: { enabled?: boolean } = {}) => {
    const trpc = useTRPC();
    return useInfiniteQuery({
        ...trpc.audit.list.infiniteQueryOptions(input, CURSOR_PAGING),
        select: (data) => data.pages.flatMap((page) => page.entries),
        meta: { quiet: true },
        placeholderData: keepPreviousData,
        enabled: opts.enabled ?? true,
    });
};

/**
 * The detail panel's own fetch — a list row no longer carries `input`, so opening one costs a
 * second round trip through `audit.get`. `id: null` (nothing selected, or the drawer just
 * closed) disables the query entirely rather than firing it with a placeholder id.
 * `placeholderData: keepPreviousData` keeps the previous row's input on screen while a newly
 * selected row's fetch is in flight, so the panel does not blank between two opens.
 */
export const useAuditEntry = (id: string | null) => {
    const trpc = useTRPC();
    return useQuery({
        ...trpc.audit.get.queryOptions({ id: id ?? '' }, { meta: { quiet: true }, enabled: id !== null }),
        placeholderData: keepPreviousData,
    });
};
